import { useState, useEffect } from 'react';
import TicketDTO, { TicketPublicSaleStatus } from '../models/ticket';
import Counter from './Counter';
import { formatCurrency } from '../utils/shared';
import TextStatus from './atoms/TextStatus';
import CouponDTO from '../types/Coupon';

export interface TicketSelection {
  ticket: TicketDTO;
  quantity: number;
  seat_name?: string;
  seat_id?: string;
}
interface Props {
  onTicketsChange: (ticketsSelection: TicketSelection[]) => void;
  tickets: TicketDTO[];
  coupon?: CouponDTO;
  title?: string;
}

interface TableTicket {
  id: string;
  name: JSX.Element;
  price: JSX.Element;
  quantity: JSX.Element;
}

const SelectTicketsTable: React.FC<Props> = ({
  onTicketsChange, tickets, coupon, title = '',
}) => {
  const [ticketsQuantity, setTicketsQuantity] = useState<TicketSelection[]>([]);
  if (!tickets) {
    throw new Error('no tickets');
  }

  useEffect(() => {
    if (tickets) {
      setTicketsQuantity(tickets.map((t) => ({ ticket: t, quantity: 0 })));
    }
  }, [tickets]);

  useEffect(() => {
    onTicketsChange(ticketsQuantity.filter((t) => t.quantity > 0));
  }, [ticketsQuantity]);

  useEffect(() => {
    if (tickets) {
      setTicketsQuantity(tickets.map((t) => ({ ticket: t, quantity: 0 })));
    }
  }, [tickets]);

  const handleQuantityUpdate = (id: string, updatedQuantity: number) => {
    const copy = [...ticketsQuantity];
    const i = copy.findIndex((t) => t.ticket.id === id);
    if (i === -1) {
      return null;
    }
    copy[i] = { ...copy[i], quantity: updatedQuantity };
    return setTicketsQuantity(copy);
  };

  const getStatusLabel = (ticket: TicketDTO) => {
    if (ticket.public_sale_status === TicketPublicSaleStatus.SoldOut) {
      return 'Agotado';
    }
    if (ticket.public_sale_status === TicketPublicSaleStatus.ComingSoon) {
      return 'Pronto';
    }
    return 'Disponible';
  };

  const getStatusClass = (ticket: TicketDTO) => {
    if (ticket.public_sale_status === TicketPublicSaleStatus.SoldOut) {
      return 'danger';
    }
    if (ticket.public_sale_status === TicketPublicSaleStatus.ComingSoon) {
      return 'warning';
    }
    return 'success';
  };

  const sortedTickets = [...tickets].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  const getPrice = (ticket: TicketDTO) => {
    if (ticket.has_open_price) {
      return '💰💰💰';
    }
    if (coupon?.value) {
      return (
        <div className="flex flex-col whitespace-nowrap">
          <span className="line-through text-xs text-text-tertiary whitespace-nowrap">{formatCurrency(ticket.price * ticket.items_create)}</span>
          <span className="whitespace-nowrap">
            {formatCurrency((ticket.price * ticket.items_create) * (1 - (coupon?.value || 0)))}
          </span>
        </div>
      );
    }
    return formatCurrency(ticket.price * ticket.items_create, 'no-decimals');
  };

  const tableTickets: TableTicket[] = sortedTickets.map((ticket) => ({
    key: ticket.id,
    id: ticket.id,
    name: (
      <div className="flex flex-col align-items-center justify-center mr-2">
        <span>{ticket.name}</span>
        <span className="text-xs text-text-secondary">
          {ticket.description?.slice(0, 50)}
          {ticket.description?.length > 50 ? '...' : ''}
        </span>
        {ticket.few_tickets_left && !(ticket.public_sale_status === TicketPublicSaleStatus.SoldOut) && (
          <span className="text-xs text-red-500">
            ¡Últimas entradas!
          </span>)}
      </div>
    ),
    price: <span className="whitespace-nowrap text-center">{getPrice(ticket)}</span>,
    quantity: ticket.public_sale_status === TicketPublicSaleStatus.Available ? (
      <div className="flex">
        <Counter
          key={ticket.id}
          upperLimit={ticket.max_selection_allowed}
          onValueChange={(newValue: number) => handleQuantityUpdate(ticket.id, newValue)}
        />
      </div>
    ) : (
      <div className="flex">
        <TextStatus type={getStatusClass(ticket)}>
          {getStatusLabel(ticket)}
        </TextStatus>
      </div>
    ),
  }));

  return (
    <div className='grid grid-cols-10 text-text-primary glass-surface p-0'>
      <h1 className='text-xl font-medium mb-2 py-2 text-center col-span-10'>{title}</h1>
      <div className='grid grid-cols-10 col-span-10 border-b border-border-primary items-center px-4 py-3'>
        <div className='col-span-4'>Entrada</div>
        <div className='col-span-3'>Precio</div>
        <div className='col-span-3'>Cantidad</div>
      </div>
      <div className='grid grid-cols-10 col-span-10'>
        {tableTickets.map((ticket) => (
          <div key={ticket.id} className={`grid grid-cols-10 col-span-10 ${tableTickets[tableTickets.length - 1].id !== ticket.id ? 'border-b border-border-primary' : ''} p-4 items-center`}>
            <div className='col-span-4'>{ticket.name}</div>
            <div className='col-span-3'>{ticket.price}</div>
            <div className='col-span-3'>{ticket.quantity}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SelectTicketsTable;
